import * as Y from 'yjs';
import EventEmitter from 'eventemitter3';

const YADA_DIR = 'yada';
const SAVE_DEBOUNCE_MS = 800;

// OPFS file names cannot contain '/'. Replace with a safe delimiter.
function toFilename(documentId: string): string {
  return documentId.replace(/\//g, '|') + '.yjs';
}

async function getYadaDir(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(YADA_DIR, { create: true });
}

export class OPFSPersistence extends EventEmitter {
  readonly documentId: string;
  readonly whenSynced: Promise<void>;

  private readonly doc: Y.Doc;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private channel: BroadcastChannel | null = null;
  private readonly tabId: string;
  private pageHideHandler: (() => void) | null = null;

  constructor(documentId: string, doc: Y.Doc) {
    super();
    this.documentId = documentId;
    this.doc = doc;
    this.tabId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    this.whenSynced = this._init();
  }

  private async _init(): Promise<void> {
    await this._load();
    this.doc.on('update', this._onUpdate);

    // Cross-tab real-time sync via BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const channelName = `yada:doc:${encodeURIComponent(this.documentId)}`;
        this.channel = new BroadcastChannel(channelName);
        this.channel.onmessage = (event) => {
          if (this.destroyed) return;
          const data = event.data;
          if (data && data.senderTabId !== this.tabId && data.update) {
            try {
              const update = new Uint8Array(data.update);
              Y.applyUpdate(this.doc, update, this);
            } catch (err) {
              console.warn('[OPFSPersistence] Failed to apply cross-tab update:', err);
            }
          }
        };
      } catch (err) {
        console.warn('[OPFSPersistence] BroadcastChannel initialization failed:', err);
      }
    }

    // Flush pending changes before page is unloaded / hidden
    if (typeof window !== 'undefined') {
      this.pageHideHandler = () => {
        if (this.saveTimer !== null) {
          clearTimeout(this.saveTimer);
          this.saveTimer = null;
          this._save().catch(() => {});
        }
      };
      window.addEventListener('pagehide', this.pageHideHandler);
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.pageHideHandler?.();
        }
      });
    }

    this.emit('synced');
  }

  private readonly _onUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this) return;
    this._scheduleSave();

    // Broadcast local update to other open browser tabs
    if (this.channel && origin !== this) {
      try {
        this.channel.postMessage({
          senderTabId: this.tabId,
          update: update.buffer.slice(update.byteOffset, update.byteOffset + update.byteLength),
        });
      } catch {
        // Non-fatal if channel posting fails
      }
    }
  };

  private _scheduleSave(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this._save().catch(err => console.warn('[OPFSPersistence] save failed', err));
    }, SAVE_DEBOUNCE_MS);
  }

  private async _load(): Promise<void> {
    try {
      const dir = await getYadaDir();
      const handle = await dir.getFileHandle(toFilename(this.documentId), { create: false });
      const file = await handle.getFile();
      if (file.size === 0) return;
      const buffer = await file.arrayBuffer();
      Y.applyUpdate(this.doc, new Uint8Array(buffer), this);
    } catch (err) {
      if ((err as DOMException)?.name !== 'NotFoundError') {
        console.warn('[OPFSPersistence] load failed', err);
      }
    }
  }

  private async _save(): Promise<void> {
    if (this.destroyed) return;
    const saveOp = async () => {
      try {
        const dir = await getYadaDir();
        const handle = await dir.getFileHandle(toFilename(this.documentId), { create: true });
        const writable = await handle.createWritable();
        const state = Y.encodeStateAsUpdate(this.doc);
        const buf = new ArrayBuffer(state.byteLength);
        new Uint8Array(buf).set(state);
        await writable.write(buf);
        await writable.close();
      } catch (err) {
        console.warn('[OPFSPersistence] save failed', err);
      }
    };

    // Use Web Locks if available to prevent concurrent OPFS file writes between tabs
    if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks?.request) {
      await navigator.locks.request(`yada:opfs:${this.documentId}`, saveOp).catch(saveOp);
    } else {
      await saveOp();
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.doc.off('update', this._onUpdate);
    if (this.channel) {
      try {
        this.channel.close();
      } catch {}
      this.channel = null;
    }
    if (this.pageHideHandler && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this._save().catch(() => {});
  }
}

export async function listAllOPFSDocumentIds(): Promise<string[]> {
  try {
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle(YADA_DIR, { create: false });
    const documentIds: string[] = [];
    for await (const name of dir.keys()) {
      if (name.endsWith('.yjs')) {
        const docId = name.slice(0, -4).replace(/\|/g, '/');
        documentIds.push(docId);
      }
    }
    return documentIds;
  } catch (err) {
    if ((err as DOMException)?.name !== 'NotFoundError') {
      console.warn('[OPFSPersistence] listAllOPFSDocumentIds failed', err);
    }
    return [];
  }
}

export async function deleteOPFSDocument(documentId: string): Promise<void> {
  try {
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle(YADA_DIR, { create: false });
    await dir.removeEntry(toFilename(documentId));
  } catch (err) {
    if ((err as DOMException)?.name !== 'NotFoundError') {
      console.warn('[OPFSPersistence] deleteOPFSDocument failed', documentId, err);
    }
  }
}

export async function clearAllOPFSDocuments(): Promise<void> {
  // Without OPFS no document was ever written here.
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return;
  try {
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle(YADA_DIR, { create: false });
    const names: string[] = [];
    for await (const name of dir.keys()) {
      if (name.endsWith('.yjs')) names.push(name);
    }
    await Promise.all(names.map(name => dir.removeEntry(name)));
  } catch (err) {
    // No yada directory means nothing was stored; anything else left files behind.
    if ((err as DOMException)?.name !== 'NotFoundError') throw err;
  }
}
